import * as React from 'react';
import Styled, { css } from 'styled-components';

import { cssDeclaration } from '../_ThemeTwo_';

export interface IRadioProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  name?: string;
  inline?: boolean;
  alert?: boolean;
  multiLine?: boolean;
}

export type IRadioCardProps = React.InputHTMLAttributes<HTMLInputElement> & Pick<IRadioProps, 'id' | 'name' | 'alert'>;

export interface IRadioContainProps {
  inline?: boolean;
}

export interface IRadioLabelProps extends React.AllHTMLAttributes<HTMLElement> {
  alert?: boolean;
}

export const RadioContain = Styled.div<IRadioContainProps>`
  width: 100%;
  position: relative;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    /* IE10+ CSS styles go here */
    input:focus + label::before {
      outline: 2px dotted #acacac;
    }
  }
`;

const RadioLabelStyles = css`
  width: 100%;
  border-radius: ${({ theme: { input } }) => input.borderRadius};
  box-shadow: ${({ theme: { radio }}) => radio.boxShadow};
  background: ${({ theme: { radio } }) => radio.colorBackground};
  color: ${({ theme: { radioCard }}) => radioCard.colorText};
  text-align: center;
  cursor: pointer;
  z-index: 200;

  &:hover {
    box-shadow: ${({ theme: { radio }}) => radio.boxShadowHover};
  }
`;

export const RadioLabel = Styled.label<IRadioLabelProps>`
  ${RadioLabelStyles}

  padding: ${({ theme: { radio } }) => radio.padding};
  border: ${({ theme: { radio } }) => radio.borderWidth} solid ${({ theme: { radio } }) => radio.colorBorder};
  color: ${({ theme: { radio } }) => radio.colorText};
  font-weight: ${({ theme: { radio } }) => radio.fontWeight};
  display: block;
  border-radius: ${({ theme: { radio }}) => radio.borderRadius && radio.borderRadius};

  /* stylelint-disable declaration-colon-newline-after */
  ${({ theme: { radio, brand }, alert }) => alert && `
    border: ${radio.borderWidth} solid ${brand.colorAlert};
    color: ${radio.colorTextAlert};
  `}
`;

export const RadioCardContent = Styled.label<IRadioLabelProps>`
  ${RadioLabelStyles}

  padding: 1.5em;
  border: ${({ theme: { radio } }) => radio.borderWidth} solid ${({ theme: { radio } }) => radio.colorBorder};

  /* IE11 Support */
  display: flex;
  flex-direction: column;

  @supports (display: grid) {
    display: block; /* Must overwrite display from Flex to fix height bug in Safari */
    flex-direction: unset;
  }

  /* Find a better solution for this if possible */
  outline-offset: -2px;


  ${({ theme }) => css`
    border-radius: ${theme.radioCard.borderRadius};
    ${theme.radioCard.border && cssDeclaration(theme.themeTwo, 'border', theme.radioCard.border)};
    ${theme.radioCard.boxShadow && cssDeclaration(theme.themeTwo, 'box-shadow', theme.radioCard.boxShadow)};
  `}

  /* stylelint-disable declaration-colon-newline-after multi-line declaration */
  ${({ theme: { radio, brand }, alert }) => alert && `
    border: ${radio.borderWidth} solid ${brand.colorAlert};
  `}
`;

export const RadioInput = Styled.input<IRadioLabelProps>`
  width: 1px;
  height: 1px;
  position: absolute;
  top: 0;
  left: -999px;
  clip: rect(1px, 1px, 1px, 1px);
  overflow: hidden;
  white-space: nowrap;

  &:focus + ${RadioCardContent} {
    outline: ${({ theme }) => theme.outline};
  }

  /* stylelint-disable */

  &:hover + ${RadioLabel},
  &:focus + ${RadioLabel} {
    background: ${({ theme: { radio } }) => radio.colorBackgroundHover};
    border: ${({ theme: { radio } }) => radio.borderWidth} solid ${({ theme: { radio } }) => radio.colorBorderHover};
    color: ${({ theme: { radio }}) => radio.colorTextHover};
  }

  &:hover + ${RadioCardContent},
  &:focus + ${RadioCardContent} {
    background: ${({ theme: { radio } }) => radio.colorBackgroundHover};
    border: ${({ theme: { radio } }) => radio.borderWidth} solid ${({ theme: { radio } }) => radio.colorBorderHover};
    color: ${({ theme: { radioCard }}) => radioCard.colorTextHover};
  }

  &:checked + ${RadioCardContent} {
    background: ${({ theme: { radioCard } }) => radioCard.colorBackgroundChecked};
    ${({ theme }) => {
      if (theme.radioCard.checked && theme.radioCard.checked.border) {
        return cssDeclaration(theme.themeTwo, 'border', theme.radioCard.checked.border);
      } else {
        return `border: ${theme.radio.borderWidth} solid ${theme.radio.colorBorderChecked}`;
      }
    }}
  }

  ${({ theme }) => theme.radio.focus && css`
    &:focus + ${RadioLabel} {
      /* stylelint-disable */
      /* outline */ ${theme.radio.focus.outline && cssDeclaration(theme.themeTwo, 'outline', theme.radio.focus.outline)};
      /* border */${theme.radio.focus.border && cssDeclaration(theme.themeTwo, 'border', theme.radio.focus.border)};
      background: ${theme.radio.focus.colorBackground && theme.radio.focus.colorBackground};
      color: ${theme.radio.focus.colorText && theme.radio.focus.colorText};

    }
  `}

  &:checked + ${RadioLabel} {
    color: ${({ theme: { radio } }) => radio.colorTextChecked};
    background: ${({ theme: { radio } }) => radio.colorBackgroundChecked};
    border: ${({ theme: { radio } }) => radio.borderWidth} solid ${({ theme: { radio } }) => radio.colorBorderChecked};
  }

  ${({ theme }) => theme.radioCard.hover && css`
    &:hover + ${RadioCardContent} {
      background-color: ${theme.radioCard.hover.colorBackground && theme.radioCard.hover.colorBackground};
      /* border */${theme.radioCard.hover.border && cssDeclaration(theme.themeTwo, 'border', theme.radioCard.hover.border)};
    }
  `}

  ${({ theme }) => theme.radio.disabled && css`
    &:disabled + ${RadioLabel} {
      cursor: not-allowed;
      color: ${theme.radio.disabled.labelColor && theme.radio.disabled.labelColor};
      background: ${theme.radio.disabled.colorBackground && theme.radio.disabled.colorBackground};
      ${theme.radio.disabled.border && cssDeclaration(theme.themeTwo, 'border', theme.radio.disabled.border)};
    }
  `}

  ${({ theme }) => theme.radioCard.focus && css`
    &:focus + ${RadioCardContent} {
      ${theme.radioCard.focus.outline && cssDeclaration(theme.themeTwo, 'outline', theme.radioCard.focus.outline)}
      ${theme.radioCard.focus.border && cssDeclaration(theme.themeTwo, 'border', theme.radioCard.focus.border)};
    }

    &:focus:checked + ${RadioCardContent} {
      border: ${theme.radio.borderWidth} solid ${theme.radio.colorBorderChecked};
    }
  `}

  ${({ theme }) => theme.radioCard.disabled && css`
    &:disabled + ${RadioCardContent} {
      cursor: not-allowed;
      background-color: ${theme.radioCard.disabled.colorBackground && theme.radioCard.disabled.colorBackground};

      ${theme.radioCard.disabled.border && cssDeclaration(theme.themeTwo, 'border', theme.radioCard.disabled.border)};
      ${theme.radioCard.disabled.boxShadow && cssDeclaration(theme.themeTwo, 'box-shadow', theme.radioCard.disabled.boxShadow)};
    }
  `}

  ${({ theme, alert }) => alert && theme.radio.alertChecked && css`
    &:checked + ${RadioLabel} {
      background-color: ${theme.radio.alertChecked.colorBackground};
      color: ${theme.radio.alertChecked.colorText && theme.radio.alertChecked.colorText};
      ${theme.radio.alertChecked.border && cssDeclaration(theme.themeTwo, 'border', theme.radio.alertChecked.border)};
    }
  `}

  ${({ theme }) => theme.radio.disabledChecked && css`
    &:disabled:checked + ${RadioLabel} {
      cursor: not-allowed;
      background-color: ${theme.radio.disabledChecked.colorBackground};
      color: ${theme.radio.disabledChecked.labelColor && theme.radio.disabledChecked.labelColor};

      ${theme.radio.disabledChecked.border && cssDeclaration(theme.themeTwo, 'border', theme.radio.disabledChecked.border)};
    }
  `}
  /* stylelint-enable */
`;
